import type { BookingSlot, SlotsByDate } from '@/features/campaigns/types/campaign';
import { enumerateDates } from '@/features/campaigns/utils/campaignUtils';

export function formatChipDate(iso: string) {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) {
    return { weekday: iso, dayMonth: '' };
  }
  return {
    weekday: d.toLocaleDateString(undefined, { weekday: 'short' }),
    dayMonth: d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
    }),
  };
}

export function formatDateList(dates: string[]): string {
  if (!dates.length) return '';
  return dates
    .map((iso) => {
      const { dayMonth } = formatChipDate(iso);
      return dayMonth || iso;
    })
    .join(', ');
}

export function normalizeDateKey(key: string): string {
  if (!key) return '';
  const isoPart = key.split('T')[0].trim();
  const parts = isoPart.split('-');
  if (parts.length === 3) {
    const y = parts[0];
    const m = parts[1].padStart(2, '0');
    const d = parts[2].padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return isoPart;
}

export function findDayDataInSlots(dateIso: string, slotsData: SlotsByDate = {}) {
  const target = normalizeDateKey(dateIso);
  for (const [key, val] of Object.entries(slotsData)) {
    if (normalizeDateKey(key) === target) {
      return val;
    }
  }
  return undefined;
}

export function isSlotAvailable(slot: unknown): boolean {
  if (!slot || typeof slot !== 'object') return false;
  const s = slot as Record<string, unknown>;
  if (s.isAvailable === false || s.isBooked === true || s.isFull === true) {
    return false;
  }
  const availableReps = Number(s.availableRepetitions ?? 0);
  return availableReps > 0;
}

/**
 * Returns dates that were loaded from availability and have no bookable capacity.
 * Days missing from `slotsData` (not yet fetched) are NOT treated as fully booked —
 * otherwise campaigns longer than the 15-day slots API window silently drop end dates.
 */
export function getFullyBookedDates(
  dates: string[],
  slotsData: SlotsByDate = {},
): string[] {
  if (!dates.length || !Object.keys(slotsData).length) return [];
  const fullyBooked: string[] = [];
  for (const d of dates) {
    const dayData = findDayDataInSlots(d, slotsData);
    // Unloaded day — keep as bookable until availability is fetched.
    if (!dayData) continue;
    if (!Array.isArray(dayData.availableSlots) || dayData.availableSlots.length === 0) {
      fullyBooked.push(d);
      continue;
    }
    const hasAnyAvail = dayData.availableSlots.some(isSlotAvailable);
    if (!hasAnyAvail) {
      fullyBooked.push(d);
    }
  }
  return fullyBooked;
}

/** Campaign dates that have available capacity but still have no booked hours. */
export function getDatesMissingSlots(
  bookingSlots: BookingSlot[],
  startDateOrDates: string | string[],
  endDate?: string,
  fullyBookedDates: string[] = [],
): string[] {
  const allDates = Array.isArray(startDateOrDates)
    ? startDateOrDates
    : startDateOrDates && endDate
      ? enumerateDates(startDateOrDates, endDate, 366)
      : [];
  if (!allDates.length) return [];

  const bookedNormalized = new Set(
    bookingSlots.map((s) => normalizeDateKey(s.date)),
  );
  const excludedNormalized = new Set(
    fullyBookedDates.map((d) => normalizeDateKey(d)),
  );

  return allDates.filter((d) => {
    const norm = normalizeDateKey(d);
    return !excludedNormalized.has(norm) && !bookedNormalized.has(norm);
  });
}

export const ALL_DATES_SLOTS_ERROR =
  'Select slots for every available campaign date, or turn on Sync slots & repetitions before continuing.';

